/**
 * Main Application Entry Point
 * Orchestrates vendor libraries and fragment initialization for the Outlook
 * add-in. Gated on Office.onReady() — the host-ready signal Outlook requires
 * before loading the task pane. SSR + HTMX means most behavior is server-
 * rendered; fragment initializers live in pages/<page>/<page>_fragment.js and
 * are imported + called here.
 */

// HTMX setup (exposes window.htmx + swaps 4xx/5xx error bodies). See vendor/htmx.js.
import { htmx } from "./vendor/htmx.js";

// Fragment initializers.
import { initConnect } from "../pages/connect/connect_fragment.js";
import { initFiles } from "../pages/files/files_fragment.js";
import { initSessionGuard } from "./utils/session.js";

// ============================================================================
// Application Initialization
// ============================================================================

Office.onReady(() => {
  // Account-type gate. Secure send runs from the pane's "Attach files securely"
  // button (creates the transfer + inserts the real link), which needs no launch
  // event — so personal Outlook.com is supported alongside Microsoft 365 (Exchange
  // Online). Other account types (enterprise on-prem/hybrid Exchange, Gmail) stay
  // blocked: untested, and the body/sessionData APIs the pane relies on aren't
  // guaranteed there. Fail closed: an unreported accountType is blocked.
  const accountType = Office.context.mailbox?.userProfile?.accountType;
  if (accountType !== "office365" && accountType !== "outlookCom") {
    htmx.ajax("GET", "/outlook/unsupported", {
      target: "#pane-content",
      swap: "innerHTML",
    });
    return;
  }

  // Session guard first: it attaches the bearer to the Files view load and
  // catches a 401 there (expired session on open) before initConnect triggers
  // that load.
  initSessionGuard();
  // initFiles before initConnect so its htmx:afterSwap listener is registered
  // before initConnect may trigger the files-view load (already-connected open).
  initFiles();
  initConnect();
  console.log("[Outlook] Leapfile Add-in loaded.");
});

// Export for external access if needed
export { htmx };
